import { useCallback, useEffect, useState } from "react";
import type { JSX } from "react";

import { useIntegrationsApi } from "../../modules/settings/hooks/useIntegrationsApi";
import { BrandMark, Icon, resolveBrand } from "../ui";
import type { IconName } from "../ui/Icon";
import { SourceConnect } from "./SourceConnect";

/// auth_types you can actually connect an account for. Sources without one
/// (e.g. the always-on `local` store, auth_type "none") are not "accounts" — they
/// are excluded from the Add-Account catalog. `api_key` sources connect here
/// too: key entry + explicit source.accounts.provision (a clean DB has zero
/// accounts until the user does this — scn_sources_accounts_001).
const CONNECTABLE = new Set(["oauth2", "phone_code", "api_key"]);

/// Per-source presentation hint (icon + circle colour). The catalog carries no
/// icon, so known sources get a recognizable glyph; everything else is generic.
function sourceIcon(sourceId: string): { name: IconName; bg: string; fg: string } {
  switch (sourceId) {
    case "telegram":
      return { name: "send", bg: "bg-[#2AABEE]", fg: "text-white" };
    case "google":
      return { name: "mail", bg: "bg-white", fg: "text-black" };
    default:
      return { name: "link", bg: "bg-surface", fg: "text-content" };
  }
}

/// A human title (capitalized source id / display name) + a short hint from the
/// surfaces it syncs, so the card reads like a product, not "Oauth2".
function titleCase(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

interface CatalogEntry {
  readonly sourceId: string;
  readonly displayName: string;
  readonly authType: string;
  readonly surfaces: readonly string[];
}

export interface AddAccountModalProps {
  readonly onClose: () => void;
  readonly onConnected?: () => void;
  /** Kept for API compatibility with the accounts panel's re-auth entry; the
   *  generic flow re-derives the connection from the provider identity, so a
   *  label is no longer threaded through. */
  readonly reauthLabel?: string;
}

export function AddAccountModal({
  onClose,
  onConnected,
}: AddAccountModalProps): JSX.Element {
  const integrationsApi = useIntegrationsApi();
  const [catalog, setCatalog] = useState<readonly CatalogEntry[]>([]);
  const [selected, setSelected] = useState<CatalogEntry | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return (): void => {
      window.removeEventListener("keydown", handler);
    };
  }, [onClose]);

  // Load the dynamic source catalog (icon-agnostic; auth_type drives the flow).
  useEffect(() => {
    let cancelled = false;
    const load = async (): Promise<void> => {
      try {
        const { sources } = await integrationsApi.getSourceList();
        if (cancelled) return;
        setCatalog(
          sources
            // Only sources you actually authorize an account for (drops `local`)
            // AND that can actually start (INV-ST-10): a source missing a
            // required operator app-cred is excluded — never a Connect that
            // fails at runtime on a missing env line.
            .filter((s) => CONNECTABLE.has(s.auth_type.toLowerCase()) && s.connectable)
            .map((s) => ({
              sourceId: s.source_id,
              displayName: s.display_name,
              authType: s.auth_type,
              surfaces: s.surfaces,
            })),
        );
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      }
    };
    void load();
    return (): void => {
      cancelled = true;
    };
  }, [integrationsApi]);

  const handleBackdrop = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose],
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-[2px]"
      onClick={handleBackdrop}
    >
      <div className="w-full max-w-md mx-4 rounded-xl bg-surface border border-edge shadow-2xl">
        <div className="flex items-center justify-between px-5 pt-5 pb-3">
          <div>
            <h2 className="text-[15px] font-semibold text-content m-0">
              {selected ? `Connect ${titleCase(selected.displayName)}` : "Add Account"}
            </h2>
            <p className="text-[13px] text-content-secondary mt-1 m-0">
              {selected
                ? "Authorize access to your data"
                : "Connect to your data in the cloud"}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-transparent border-none text-content-secondary hover:text-content hover:bg-surface-hover cursor-pointer"
          >
            ✕
          </button>
        </div>

        <div className="px-5 pb-5">
          {selected ? (
            <SourceConnect
              sourceId={selected.sourceId}
              authType={selected.authType}
              onConnected={() => {
                onConnected?.();
                onClose();
              }}
              onError={setError}
            />
          ) : (
            <div className="rounded-xl bg-surface-tertiary border border-edge overflow-hidden divide-y divide-edge">
              {catalog.map((entry) => {
                const brand = resolveBrand(entry.sourceId);
                const glyph = sourceIcon(entry.sourceId);
                return (
                  <button
                    key={entry.sourceId}
                    type="button"
                    onClick={() => {
                      setError(null);
                      setSelected(entry);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3.5 text-left cursor-pointer hover:bg-surface-hover transition-colors border-none bg-transparent"
                  >
                    {brand ? (
                      <BrandMark brand={brand} size={36} />
                    ) : (
                      <div
                        className={`w-9 h-9 rounded-full ${glyph.bg} flex items-center justify-center shrink-0`}
                      >
                        <Icon name={glyph.name} size={18} className={glyph.fg} />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-content">
                        {titleCase(entry.displayName)}
                      </div>
                      <div className="text-xs text-content-secondary truncate">
                        {entry.surfaces.join(" · ")}
                      </div>
                    </div>
                    <Icon
                      name="chevron-right"
                      size={16}
                      className="text-content-muted shrink-0"
                    />
                  </button>
                );
              })}
              {catalog.length === 0 && !error && (
                <div className="px-4 py-3.5 text-sm text-content-secondary">
                  No sources available.
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="mt-3 rounded-lg bg-red-500/10 border border-red-500/30 px-4 py-3">
              <span className="text-red-400 text-sm">{error}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
